// Familial aggregation: relatives share a diagnosis, and that is ALL that is
// claimed.
//
// This is a DIFFERENT CONCEPT from a mode of inheritance, not a weaker grade of
// one. It exists because the honest answer for a great many diseases is "these
// relatives share this diagnosis" with no mechanism attached — and the engine
// previously had no way to say that, so it said "autosomal dominant, Strong"
// instead.
//
// The two cases that motivated it:
//
//   - A multifactorial susceptibility locus. Asthma susceptibility and atopic
//     dermatitis are common complex traits; a mode of inheritance is not a
//     meaningful claim about either, and labelling one Strong invites a
//     clinician to manage a common trait as a segregating Mendelian condition.
//   - A somatically-acquired disease. Primary myelofibrosis is overwhelmingly
//     an acquired neoplasm driven by somatic JAK2 mutation. Familial
//     clustering of myeloproliferative neoplasms is nonetheless real, and
//     germline predisposition loci have been described. So the disease must
//     NOT be removed from the panel — what must go is the conflation of
//     "relatives are affected" with "the disease segregates".
//
// Everything here is an observation plus its limits. No confidence grade, no
// mechanism, no recommendation.

import {
  affectedList,
  affectedChainGenerations,
  verticalPairs,
  consanguineousLoopsInAffectedAncestry,
  pidsOf
} from './inheritance.helpers';
import { NO_MODE_REASON, OUTPUT_KIND } from './inheritance';

// Plain-language account of why no mode is asserted. Phrased as a statement
// about the DISEASE or the RECORD, never as a shortcoming of the software —
// a clinician reading "no inheritance annotation" should understand that the
// reference is silent, not that a lookup failed.
const REASON_TEXT = Object.freeze({
  [NO_MODE_REASON.clinicianSporadic]:
    'Recorded as sporadic or multifactorial for this family, so no mode of inheritance is stated.',
  [NO_MODE_REASON.susceptibilityLocus]:
    'This is a susceptibility locus rather than a single-gene disorder, so a mode of inheritance is not a meaningful claim about it.',
  [NO_MODE_REASON.somaticEntry]:
    'This entry describes a somatically acquired condition, so it is not transmitted in a Mendelian pattern.',
  [NO_MODE_REASON.referenceNonMendelian]:
    'The reference data records this condition as non-Mendelian.',
  [NO_MODE_REASON.referenceConflicting]:
    'The reference data annotates this condition both as Mendelian and as non-Mendelian, so no single mode is established.',
  [NO_MODE_REASON.referenceSilent]:
    'No mode of inheritance is annotated for this condition, so none is asserted.'
});

const reasonText = (reason) =>
  REASON_TEXT[reason] ||
  'No mode of inheritance is established for this condition.';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Describe the familial pattern of a disease WITHOUT claiming a mechanism.
 *
 * @param {object} ped tokenised pedigree
 * @param {string} diseaseId
 * @param {object} gate result of `inheritanceAssertionGate` — carries why no
 *   mode may be asserted
 * @param {object} [meta] disease metadata, for the disease name
 * @returns {object|null} null when nobody in the family is affected — an
 *   absence is not an aggregation
 */
export function detectFamilialAggregation(ped, diseaseId, gate, meta = null) {
  const affected = affectedList(ped, diseaseId);
  if (affected.length === 0) return null;

  const generations = affectedChainGenerations(ped, diseaseId);
  const vertical = verticalPairs(ped, diseaseId);
  // Lineage-scoped, not whole-pedigree: a consanguineous union in an
  // unrelated branch says nothing about THIS disease (PA-6).
  const consanguineous =
    consanguineousLoopsInAffectedAncestry(ped, diseaseId).length > 0;

  const observations = [];

  observations.push({
    key: 'affected_count',
    text:
      affected.length === 1
        ? 'One affected relative recorded.'
        : `${plural(
            affected.length,
            'affected relative',
            'affected relatives'
          )} recorded.`
  });

  if (generations > 1) {
    observations.push({
      key: 'generations',
      // Deliberately "appears in" rather than "transmitted through": the
      // whole point is that no transmission is being claimed.
      text: `Affected relatives appear in ${plural(
        generations,
        'generation',
        'generations'
      )}.`
    });
  }

  if (vertical > 0) {
    observations.push({
      key: 'parent_child_pairs',
      text: `${plural(
        vertical,
        'affected parent-and-child pair',
        'affected parent-and-child pairs'
      )} recorded. Clustering of this kind can arise without Mendelian transmission.`
    });
  }

  if (consanguineous) {
    observations.push({
      key: 'consanguinity',
      text: 'A consanguineous union is recorded in the affected lineage.'
    });
  }

  const limitations = [
    { key: 'no_mode', text: reasonText(gate?.reason) },
    {
      key: 'aggregation_only',
      text: 'Shared diagnosis among relatives is reported here as familial aggregation only. It does not establish that the condition segregates in this family, and no recurrence risk is derived from it.'
    }
  ];

  // A single affected relative is not aggregation at all — say so rather than
  // letting one case read as a family pattern.
  if (affected.length === 1) {
    limitations.push({
      key: 'single_case',
      text: 'A single affected relative is insufficient to describe a familial pattern.'
    });
  }

  const statement =
    affected.length === 1
      ? 'One affected relative recorded'
      : `${plural(affected.length, 'affected relative', 'affected relatives')}${
          generations > 1
            ? ` across ${plural(generations, 'generation', 'generations')}`
            : ''
        }`;

  return {
    kind: OUTPUT_KIND.familialAggregation,
    diseaseId,
    diseaseName: meta?.name || null,
    supportingIds: pidsOf(affected),
    // Structured, so a surface renders the observation and its limits together
    // and cannot show one without the other.
    evidence: {
      statement,
      limitation: limitations[0].text,
      observations,
      limitations
    },
    // Why no mode was asserted, for filtering and for the audit trail.
    noModeReason: gate?.reason || null,
    noModeSource: gate?.source || null,
    counts: {
      affected: affected.length,
      generations,
      verticalPairs: vertical
    },
    // Flattened, most-important-first, for surfaces that render a line list.
    rationale: [...observations, ...limitations].map((e) => e.text)
  };
}
